import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clientsApi } from "@/api/endpoints";
import { ApiError } from "@/api/client";
import { Modal } from "@/components/Modal";
import { CUSTOMER_STATUSES, US_STATES } from "@/constants/crm";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: (customerId: string) => void;
};

const inputStyle = {
  width: "100%",
  padding: "0.6rem 0.75rem",
  borderRadius: 10,
  border: "1px solid var(--border)",
  background: "var(--surface, #fff)",
};

const label = { display: "grid", gap: "0.3rem" } as const;

const EMPTY = {
  first_name: "",
  last_name: "",
  company_name: "",
  date_of_birth: "",
  email: "",
  phone: "",
  customer_status: "prospect",
  lead_source: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  zip: "",
};

export function NewClientModal({ open, onClose, onCreated }: Props) {
  const navigate = useNavigate();
  const [entityType, setEntityType] = useState<"individual" | "business">("individual");
  const [form, setForm] = useState({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);
  const [duplicateId, setDuplicateId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function update(field: keyof typeof form, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function reset() {
    setForm({ ...EMPTY });
    setEntityType("individual");
    setError(null);
    setDuplicateId(null);
    setSaving(false);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setDuplicateId(null);
    const hasName =
      entityType === "individual"
        ? form.first_name.trim() || form.last_name.trim()
        : form.company_name.trim();
    if (!hasName && !form.email.trim()) {
      setError("Provide at least a name/company or email.");
      return;
    }
    setSaving(true);
    try {
      const address = Object.fromEntries(
        (["line1", "line2", "city", "state", "zip"] as const)
          .map((k) => [k, form[k].trim()])
          .filter(([, v]) => v !== ""),
      );
      const payload: Record<string, unknown> = {
        entity_type: entityType,
        customer_status: form.customer_status,
      };
      if (entityType === "individual") {
        if (form.first_name.trim()) payload.first_name = form.first_name.trim();
        if (form.last_name.trim()) payload.last_name = form.last_name.trim();
        if (form.date_of_birth) payload.date_of_birth = form.date_of_birth;
      } else if (form.company_name.trim()) {
        payload.company_name = form.company_name.trim();
      }
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.phone.trim()) payload.phone = form.phone.trim();
      if (form.lead_source.trim()) payload.lead_source = form.lead_source.trim();
      if (Object.keys(address).length) payload.address = address;

      const created = await clientsApi.create(payload);
      reset();
      onClose();
      if (onCreated) onCreated(created.id);
      else navigate(`/clients/${created.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const detail = err.payload?.detail as { message?: string; customer_id?: string } | undefined;
        setDuplicateId(detail?.customer_id ?? null);
        setError(detail?.message ?? "A client with this email already exists.");
      } else {
        setError(err instanceof Error ? err.message : "Failed to create client.");
      }
      setSaving(false);
    }
  }

  return (
    <Modal open={open} title="New client" onClose={onClose} width="38rem">
      <form onSubmit={onSubmit} style={{ display: "grid", gap: "0.85rem" }}>
        <div style={{ display: "inline-flex", gap: "0.4rem" }}>
          {(["individual", "business"] as const).map((t) => (
            <button
              key={t}
              type="button"
              className={`btn ${entityType === t ? "btn-primary" : "btn-secondary"}`}
              onClick={() => setEntityType(t)}
            >
              {t === "individual" ? "Individual" : "Business"}
            </button>
          ))}
        </div>

        {entityType === "individual" ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 0.9fr", gap: "0.75rem" }}>
            <label style={label}>
              <span className="field-label">First name</span>
              <input style={inputStyle} value={form.first_name} onChange={(e) => update("first_name", e.target.value)} />
            </label>
            <label style={label}>
              <span className="field-label">Last name</span>
              <input style={inputStyle} value={form.last_name} onChange={(e) => update("last_name", e.target.value)} />
            </label>
            <label style={label}>
              <span className="field-label">Date of birth</span>
              <input style={inputStyle} type="date" value={form.date_of_birth} onChange={(e) => update("date_of_birth", e.target.value)} />
            </label>
          </div>
        ) : (
          <label style={label}>
            <span className="field-label">Business / entity name</span>
            <input style={inputStyle} value={form.company_name} onChange={(e) => update("company_name", e.target.value)} />
          </label>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <label style={label}>
            <span className="field-label">Email</span>
            <input style={inputStyle} type="email" value={form.email} onChange={(e) => update("email", e.target.value)} />
          </label>
          <label style={label}>
            <span className="field-label">Phone</span>
            <input style={inputStyle} value={form.phone} onChange={(e) => update("phone", e.target.value)} />
          </label>
        </div>

        <div style={{ borderTop: "1px solid var(--border)", paddingTop: "0.6rem" }}>
          <span className="field-label" style={{ display: "block", marginBottom: "0.5rem" }}>Mailing address</span>
          <div style={{ display: "grid", gap: "0.6rem" }}>
            <input style={inputStyle} placeholder="Street address" value={form.line1} onChange={(e) => update("line1", e.target.value)} />
            <input style={inputStyle} placeholder="Apt / Suite (optional)" value={form.line2} onChange={(e) => update("line2", e.target.value)} />
            <div style={{ display: "grid", gridTemplateColumns: "1.6fr 0.8fr 0.9fr", gap: "0.6rem" }}>
              <input style={inputStyle} placeholder="City" value={form.city} onChange={(e) => update("city", e.target.value)} />
              <select style={inputStyle} value={form.state} onChange={(e) => update("state", e.target.value)}>
                <option value="">State</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <input style={inputStyle} placeholder="ZIP" value={form.zip} onChange={(e) => update("zip", e.target.value)} />
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <label style={label}>
            <span className="field-label">Status</span>
            <select style={inputStyle} value={form.customer_status} onChange={(e) => update("customer_status", e.target.value)}>
              {CUSTOMER_STATUSES.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </label>
          <label style={label}>
            <span className="field-label">Lead source</span>
            <input style={inputStyle} value={form.lead_source} onChange={(e) => update("lead_source", e.target.value)} />
          </label>
        </div>

        {error ? (
          <div className="error-banner">
            {error}
            {duplicateId ? (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ marginLeft: "0.5rem" }}
                onClick={() => {
                  onClose();
                  navigate(`/clients/${duplicateId}`);
                }}
              >
                Open existing client
              </button>
            ) : null}
          </div>
        ) : null}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.25rem" }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Creating…" : "Create client"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
